import React from "react";

import { useApolloClient } from "@apollo/client";

import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_MISSION, { UPDATE_MISSION } from "../../provider/Missions/mutation";
import { MISSIONS } from "../../provider/Missions/query";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";
import moment from "moment";

const Crud = () => {
    const settings = useSettings();
    const apollo = useApolloClient();

    const toast = React.useRef<any>(null);
    const dt = React.useRef<any>(null);

    const [records, setRecords] = React.useState<any[]>([]);
    const [record, setRecord] = React.useState<iMission>();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
    const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [globalFilter, setGlobalFilter] = React.useState<any>(null);

    let emptyRecord: iMission = {
        name: "",
        city: "",
        end_date: "",
        start_date: "",
        is_actived: true,
    };

    const loadRecords = (cleanRecord: boolean = false) => {
        setLoading(true);

        apollo
            .query({
                query: MISSIONS,
                variables: {
                    // limit: perPage,
                    // filter: filter,
                },
                fetchPolicy: "no-cache",
            })
            .then(response => {
                const data = response.data.missions;

                setRecords(data);
                setLoading(false);
            })
            .catch(error => {
                setRecords([]);
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const openNew = () => {
        setRecord(emptyRecord);
        setSubmitted(false);
        setRecordDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRecordDialog(false);
    };

    const hideDeleteRecordDialog = () => {
        setDeleteRecordDialog(false);
    };

    const saveRecord = () => {
        setSubmitted(true);

        if (!record?.name || !record.city || !record.start_date || !record.end_date) {
            return;
        }

        const _record: any = { ...record };

        delete _record.__typename;

        setLoading(true);
        let mutation = CREATE_MISSION;

        if (record?.id) {
            _record.id = parseInt(`${_record.id}`);
            mutation = UPDATE_MISSION;

            delete _record.key;
        }

        apollo
            .mutate({
                mutation,
                variables: {
                    data: {
                        ..._record,
                    },
                },
            })
            .then(response => {
                setLoading(false);

                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

                loadRecords();
                setRecordDialog(false);
                setRecord(emptyRecord);
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const editRecord = (record: iMission) => {
        const _record: iMission = { ...record };

        setRecord({ ..._record });
        setRecordDialog(true);
    };

    const confirmDeleteRecord = (record: any) => {
        setRecord(record);
        setDeleteRecordDialog(true);
    };

    const deleteRecord = () => {
        apollo
            .mutate({
                mutation: UPDATE_MISSION,
                variables: {
                    data: {
                        id: parseInt(`${record?.id}`),
                        is_deleted: true,
                    },
                },
            })
            .then(response => {
                let _records = records.filter((val: any) => val.id !== record?.id);
                setRecords(_records);
                setDeleteRecordDialog(false);
                setRecord(emptyRecord);
                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const onInputChange = (e: any, name: string) => {
        let val = (e.target && e.target.value) || "";

        let _record: any = { ...record };
        _record[name] = val;

        setRecord(_record);
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
                {/* <Button
                    label="Delete"
                    icon="pi pi-trash"
                    className="p-button-danger mb-2"
                    onClick={confirmDeleteSelected}
                    disabled={!selectedRecords || !selectedRecords.length}
                /> */}
            </React.Fragment>
        );
    };

    const codeBodyTemplate = (rowData: iMission) => {
        return (
            <>
                <span className="p-column-title">Chave.</span>
                {rowData.id}
            </>
        );
    };

    const nameBodyTemplate = (rowData: iMission) => {
        return (
            <>
                <span className="p-column-title">Nome</span>
                {rowData.name}
            </>
        );
    };

    const cityBodyTemplate = (rowData: iMission) => {
        return (
            <>
                <span className="p-column-title">Cidade</span>
                {rowData.city}
            </>
        );
    };

    const dateBodyTemplate = (rowData: iMission) => {
        return (
            <>
                <span className="p-column-title">Data</span>
                {moment(rowData.start_date).utc().format("DD/MM/YYYY")} À {moment(rowData.end_date).utc().format("DD/MM/YYYY")}
            </>
        );
    };

    const actionBodyTemplate = (rowData: iMission) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{loading ? "Carregando missões..." : "Missões"}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
            </span>
        </div>
    );

    const recordDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
            <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
        </>
    );

    const deleteRecordDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
            <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
        </>
    );

    React.useEffect(() => {
        loadRecords(true);
    }, []);

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={records}
                        dataKey="id"
                        paginator
                        stripedRows={true}
                        rowHover
                        showGridlines
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        globalFilter={globalFilter}
                        emptyMessage="Nenhum registro encontrado"
                        header={header}
                        responsiveLayout="scroll">
                        <Column field="id" header="Chave" sortable body={codeBodyTemplate} headerStyle={{}}></Column>
                        <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
                        <Column field="city" header="Cidade" body={cityBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="start_date" header="Data" body={dateBodyTemplate} sortable headerStyle={{}}></Column>

                        <Column header="Ações" className="flex flex-row align-items-start justify-content-start" body={actionBodyTemplate}></Column>
                    </DataTable>

                    {/* Modal adicionar editar */}
                    <Dialog
                        visible={recordDialog}
                        style={{ width: "550px" }}
                        header={record?.id ? "Detalhes da Missão" : "Nova Missão"}
                        modal
                        className="p-fluid"
                        footer={recordDialogFooter}
                        onHide={hideDialog}>
                        <div className="flex flex-column md:flex-row flex-wrap">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="name">* Nome</label>
                                <InputText
                                    id="name"
                                    value={record?.name}
                                    onChange={e => onInputChange(e, "name")}
                                    required
                                    autoFocus
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                                />
                                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="city">* Cidade</label>
                                <InputText
                                    id="city"
                                    value={record?.city}
                                    onChange={e => onInputChange(e, "city")}
                                    required
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.city })}
                                />
                                {submitted && !record?.city && <small className="p-invalid">Cidade é obrigatório.</small>}
                            </div>
                        </div>
                        <div className="flex flex-column md:flex-row flex-wrap ">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="start_date">* Data início</label>
                                <InputText
                                    id="start_date"
                                    type={"date"}
                                    value={record?.start_date}
                                    autoComplete={"new-password"}
                                    onChange={e => onInputChange(e, "start_date")}
                                    required
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.start_date })}
                                />
                                {submitted && record?.start_date === "" && <small className="p-invalid">Data de início é obrigatório.</small>}
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="end_date">* Data fim</label>
                                <InputText
                                    id="end_date"
                                    type={"date"}
                                    value={record?.end_date}
                                    autoComplete={"new-password"}
                                    onChange={e => onInputChange(e, "end_date")}
                                    required
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.end_date })}
                                />
                                {submitted && record?.end_date === "" && <small className="p-invalid">Data fim é obrigatório.</small>}
                            </div>
                        </div>
                    </Dialog>

                    {/* Modal remover registro unico */}
                    <Dialog
                        visible={deleteRecordDialog}
                        // style={{ width: "450px" }}
                        header="Atenção"
                        modal
                        footer={deleteRecordDialogFooter}
                        onHide={hideDeleteRecordDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {record && (
                                <span>
                                    Tem certeza de que deseja excluir <b>{record?.name}</b>?
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Crud;
